import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import APIS from "@service/api";
import { getUser } from "@utils";

export const accountManagementApi = createApi({
	reducerPath: "accountManagementApi",
	refetchOnFocus: false,
	refetchOnReconnect: true,
	refetchOnMountOrArgChange: false,
	tagTypes: ["UserProfile", "LastLogin", "DailyMaxDeposit", "ResponsibleGamingLimits"],
	baseQuery: fetchBaseQuery({
		baseUrl: APIS["account-api"],
		prepareHeaders: (headers) => {
			const user = getUser();

			if (user?.token) {
				headers.set("Authorization", `Bearer=${user.token}`);
			}

			return headers;
		},
	}),
	endpoints: (builder) => ({
		getUserProfile: builder.query({
			query: () => ({
				url: "profile",
				method: "GET",
			}),
			providesTags: ["UserProfile"],
		}),
		getLastLogin: builder.query({
			query: () => ({
				url: "lastLogin",
				method: "GET",
			}),
			providesTags: ["LastLogin"],
		}),
		getDailyMaxDeposit: builder.query({
			query: () => ({
				url: "dailymaxdeposit",
				method: "GET",
			}),
			providesTags: ["DailyMaxDeposit"],
		}),
		setDailyMaxDeposit: builder.mutation({
			query: ({ maxDailyDeposit }) => ({
				url: "dailymaxdeposit",
				method: "PUT",
				body: { maxDailyDeposit },
			}),
			invalidatesTags: ["DailyMaxDeposit"],
		}),
		deleteDailyMaxDeposit: builder.mutation({
			query: () => ({
				url: "dailymaxdeposit",
				method: "DELETE",
			}),
			invalidatesTags: ["DailyMaxDeposit"],
		}),
		updateUserProfile: builder.mutation({
			query: (payload) => ({
				url: "profile",
				method: "PUT",
				body: payload,
			}),
			invalidatesTags: ["UserProfile"],
		}),
		getAccountInsightsSummary: builder.query({
			query: () => ({
				url: "account-insights/summary",
				method: "GET",
			}),
		}),
		getAccountInsightsSessionActivity: builder.query({
			query: ({ from, to, interval = "day", limit = 50, startKey } = {}) => ({
				url: "account-insights/session-activity",
				method: "GET",
				params: {
					...(from != null && { from }),
					...(to != null && { to }),
					interval,
					limit,
					...(startKey != null && { startKey }),
				},
			}),
		}),
		getAccountInsightsFinancialHistory: builder.query({
			query: ({ from, to, interval = "day" } = {}) => ({
				url: "account-insights/financial-history",
				method: "GET",
				params: {
					...(from != null && { from }),
					...(to != null && { to }),
					interval,
				},
			}),
		}),
		getResponsibleGamingLimits: builder.query({
			query: () => ({
				url: "responsible-gaming/limits",
				method: "GET",
			}),
			providesTags: ["ResponsibleGamingLimits"],
		}),
		getResponsibleGamingLimit: builder.query({
			query: ({ type, period = "rolling_24h" }) => ({
				url: `responsible-gaming/limits/${type}`,
				method: "GET",
				params: { period },
			}),
			providesTags: ["ResponsibleGamingLimits"],
		}),
		setResponsibleGamingLimit: builder.mutation({
			query: ({ type, period = "rolling_24h", value }) => ({
				url: `responsible-gaming/limits/${type}`,
				method: "PUT",
				body: { period, value },
			}),
			invalidatesTags: ["ResponsibleGamingLimits"],
		}),
		deleteResponsibleGamingLimit: builder.mutation({
			query: ({ type, period = "rolling_24h" }) => ({
				url: `responsible-gaming/limits/${type}`,
				method: "DELETE",
				params: { period },
			}),
			invalidatesTags: ["ResponsibleGamingLimits"],
		}),
	}),
});

export const {
	useGetUserProfileQuery,
	useGetLastLoginQuery,
	useGetDailyMaxDepositQuery,
	useSetDailyMaxDepositMutation,
	useDeleteDailyMaxDepositMutation,
	useUpdateUserProfileMutation,
	useGetAccountInsightsSummaryQuery,
	useGetAccountInsightsSessionActivityQuery,
	useLazyGetAccountInsightsSessionActivityQuery,
	useGetAccountInsightsFinancialHistoryQuery,
	useGetResponsibleGamingLimitsQuery,
	useGetResponsibleGamingLimitQuery,
	useSetResponsibleGamingLimitMutation,
	useDeleteResponsibleGamingLimitMutation,
} = accountManagementApi;
